import React, { Component } from 'react';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import { aggregators } from 'react-pivottable/Utilities';
import pivot from '../css/module/pivotTable.css';
import { cellFormatter } from '../util/helpers/chart-formatting-helpers';
import DataTableCell from '../util/DataTableCell';
import {
  PIVOT_CUSTOM_HEADINGS,
  PIVOT_SHOW_TOTALS,
} from './ChartFormattingOptions';
import DataTableHeading from '../util/DataTableHeading';
import PivotTableHeading from '../util/PivotTableHeading';
import TrevorTable from './pivotTable/trevorTableRenderers';
import { PIVOT_COLUMNS_ID, PIVOT_ROWS_ID } from './PivotColumnsRowsSelection';
import { isPivotColumnTypeSubtotalsSupported } from './charting/ChartingConfiguration';

class PivotTable extends Component {
  /**
   * This function waits until an element is present in the DOM using a
   * selector to identify it. Uses a Promise that is fulfilled when the
   * element is present.
   *
   * @param selectorWait
   * 		Selector used to identify the element we are waiting for
   * @param selectorReturn
   * 		Once the "selectorWait" element is present, we can return a different
   * 		one using this other selector (or return the same one)
   * @returns {Promise<unknown>}
   */
  waitForElm(selectorWait, selectorReturn) {
    return new Promise((resolve) => {
      if (document.querySelector(selectorWait)) {
        // eslint-disable-next-line no-promise-executor-return
        return resolve(document.querySelectorAll(selectorReturn));
      }

      const observer = new MutationObserver(() => {
        if (document.querySelector(selectorWait)) {
          resolve(document.querySelectorAll(selectorReturn));
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }

  /**
   * Hack to make heading rows and columns to be sticky during scroll.
   * This function is called after componentDidMount and componentDidUpdate,
   * however even at this point the pivot table may not be completely rendered
   * so we use a promise and wait until is fully rendered to perform this
   * operation.
   */
  updateTableCellsStickyBehavior() {
    const { identifier } = this.props;

    // Once all the tbody rows are rendered, proceeds to add the necessary styles to make the first columns sticky
    this.waitForElm(
      `div#pivot-${identifier} table.pvtTable tbody th.pvtRowLabel`,
      `div#pivot-${identifier} table.pvtTable tbody tr`,
    ).then((elms) => {
      $(window).trigger('resize'); // This fake resize helps to correctly calculate cell widths
      const columnsLeft = [];
      elms.forEach((row) => {
        const columns = $(row).find('th.pvtRowLabel');
        let aggregatedWidth = 0;
        // Handle the case when this row has less columns because the previous one had a column with rowspan
        const extraColumnIndex = columnsLeft.length - columns.length;
        columns.each(function (i) {
          const columnIndex = i + extraColumnIndex;
          if (!columnsLeft[columnIndex]) {
            columnsLeft[columnIndex] = aggregatedWidth;
          }
          $(this).css('left', columnsLeft[columnIndex]);
          aggregatedWidth += $(this).outerWidth();
        });
      });
    });
    // Once all the thead rows are rendered, proceeds to add the necessary styles to make the rows sticky
    this.waitForElm(
      `div#pivot-${identifier} table.pvtTable thead tr`,
      `div#pivot-${identifier} table.pvtTable thead tr`,
    ).then((elms) => {
      $(window).trigger('resize'); // This fake resize helps to correctly calculate cell widths
      let aggregatedHeight = 0;
      const columnsLeft = [];
      let rowspan = 0;
      elms.forEach((row, i) => {
        $(row).css('top', aggregatedHeight);
        // Hack to make sure heading rows have the correct z-index when scrolling
        const dataCells = $(row).children('th.pvtColLabel').length;
        if (dataCells > 0) {
          $(row).css('z-index', 3 + elms.length - i);
        } else {
          $(row).css('z-index', 4 + elms.length - i);
        }
        aggregatedHeight += $(row).height();
        let aggregatedWidth = 0;
        // Make the "corner" cells be sticky for both vertical and horizontal scroll
        const columns = $(row).find('th.pvtAxisLabel');
        if (i === 0) {
          // Special case, the most corner cell doesn't have a class name so we manually look for it
          const first = $($(row).children()[0]);
          columnsLeft[0] = 0;
          if ($(first).prop('classList').length === 0) {
            aggregatedWidth += first.outerWidth();
          }
          first.css('left', 0);
          rowspan = first.attr('rowspan') ? first.attr('rowspan') : 0;
        }
        columns.each(function (j) {
          const sibling = $(this).prev();
          const columnIndex =
            i === 0 ? sibling.length : j + sibling.length + rowspan;
          if (columnsLeft[columnIndex] === undefined) {
            columnsLeft[columnIndex] = aggregatedWidth;
          }

          aggregatedWidth += $(this).outerWidth();
          $(this).css('left', columnsLeft[columnIndex]);
          $(this).css('position', 'sticky');
          // Handle "total" heading cell
          if (i === elms.length - 1) {
            const next = $(this).next('th.pvtTotalLabel');
            if (next) {
              $(next).css('left', aggregatedWidth);
              $(next).css('position', 'sticky');
            }
          }
        });
        rowspan = rowspan > 0 ? rowspan - 1 : rowspan;
      });
    });
    clearTimeout(this.timeoutId);
  }

  componentDidMount() {
    // This timeout is useful to guarantee that the inner table is really ready and every cell has rendered completely
    this.timeoutId = setTimeout(() => this.updateTableCellsStickyBehavior(), 1);
  }

  componentDidUpdate() {
    // This timeout is useful to guarantee that the inner table is really ready and every cell has rendered completely
    this.timeoutId = setTimeout(() => this.updateTableCellsStickyBehavior(), 1);
  }

  showTotalCells(forColumns, forRows, headerLabel) {
    const { identifier } = this.props;

    this.waitForElm(
      `div#pivot-${identifier} table.pvtTable tbody td.pvtTotal`,
      `div#pivot-${identifier} table.pvtTable tbody td.pvtTotal`,
    ).then(() => {
      this.hideTotalCells();
      const totalsLabel = headerLabel || 'Total';
      if (forRows) {
        $(`div#pivot-${identifier} table.pvtTable thead th.pvtTotalLabel`).css({
          display: 'table-cell',
          position: 'sticky',
          right: 0,
        });
        $(`div#pivot-${identifier} table.pvtTable tbody tr td.pvtTotal`).css({
          display: 'table-cell',
          'font-weight': 'normal',
          position: 'sticky',
          right: 0,
        });
        $(
          `div#pivot-${identifier} table.pvtTable tbody tr:last-of-type td.pvtTotal`,
        ).css('display', 'none');
        $(
          `div#pivot-${identifier} table.pvtTable tbody tr:last-of-type th.pvtTotalLabel`,
        ).css('display', 'none');
        if (!forColumns) {
          $(
            `div#pivot-${identifier} table.pvtTable thead th.pvtTotalLabel:not(:empty)`,
          ).html(totalsLabel);
        }
      }
      if (forColumns) {
        $(
          `div#pivot-${identifier} table.pvtTable tbody tr:last-of-type th.pvtTotalLabel`,
        ).css('display', 'table-cell');
        $(
          `div#pivot-${identifier} table.pvtTable tbody tr:last-of-type td.pvtTotal`,
        )
          .css({
            display: 'table-cell',
            'font-weight': 'normal',
          })
          .parent()
          .css({
            position: 'sticky',
            bottom: 0,
            'z-index': 10,
          });

        if (!forRows) {
          $(
            `div#pivot-${identifier} table.pvtTable tbody tr:last-of-type th.pvtTotalLabel:not(:empty)`,
          ).html(totalsLabel);
        }
      }
      if (forColumns && forRows) {
        $(`div#pivot-${identifier} table.pvtTable tbody td.pvtGrandTotal`).css({
          display: 'table-cell',
          position: 'sticky',
          right: 0,
          bottom: 0,
          'z-index': 15,
        });
      } else {
        $(`div#pivot-${identifier} table.pvtTable tbody td.pvtGrandTotal`).css(
          'display',
          'none',
        );
      }
      if (!headerLabel) {
        $(`div#pivot-${identifier} table.pvtTable tbody tr td.pvtTotal`).css(
          'font-weight',
          'bold',
        );
        $(
          `div#pivot-${identifier} table.pvtTable tbody tr:last-of-type td.pvtTotal`,
        ).css('font-weight', 'bold');
      }
    });
  }

  hideTotalCells() {
    const { identifier } = this.props;
    [
      `div#pivot-${identifier} table.pvtTable tbody td.pvtTotal`,
      `div#pivot-${identifier} table.pvtTable tbody th.pvtTotalLabel`,
      `div#pivot-${identifier} table.pvtTable thead th.pvtTotalLabel`,
      `div#pivot-${identifier} table.pvtTable tbody td.pvtGrandTotal`,
    ].forEach((selector) => $(selector).css('display', 'none'));

    $(
      `div#pivot-${identifier} table.pvtTable thead th.pvtTotalLabel:empty`,
    ).css('display', 'table-cell');
  }

  sameHeading(e, e1) {
    return (
      e.columnName().alias() === e1.columnName().alias() &&
      e.columnName().name() === e1.columnName().name()
    );
  }

  getColumns(customColumns, dataTable) {
    const defaultHeadings = dataTable
      .headings()
      .slice(0, dataTable.headings().length - 1);
    // Remove columns that were previously in the query and are not in the headings now
    return customColumns.filter(
      (e) => defaultHeadings.findIndex((e1) => this.sameHeading(e, e1)) >= 0,
    );
  }

  getRows(customRows, customColumns, dataTable) {
    const defaultHeadings = dataTable
      .headings()
      .slice(0, dataTable.headings().length - 1);
    const customData = [...customColumns, ...customRows];
    // Add new headings that were not previously in the query as a new row
    const newRows = [
      ...customRows,
      ...defaultHeadings.filter((e) => {
        return customData.findIndex((e1) => this.sameHeading(e, e1)) === -1;
      }),
    ];
    // Remove rows that were previously in the query and are not in the headings now
    return newRows.filter(
      (e) => defaultHeadings.findIndex((e1) => this.sameHeading(e, e1)) >= 0,
    );
  }

  buildSorters(columns, rows) {
    const sorters = {};
    const getDefaultSortCriteria = (item) => {
      const is3D = this.props.dataTable.headings().length === 3;
      const is4D = this.props.dataTable.headings().length === 4;
      const itemIndex = this.props.dataTable
        .headings()
        .findIndex((e1) => this.sameHeading(item.heading, e1));

      // This sort options come from the way columns are sorted by default in the SQL query
      const sorts = is3D
        ? [-1, 0, -1]
        : is4D
        ? [-1, 1, 0, -1]
        : [-1, 1, 1, 0, -1];

      return sorts[itemIndex];
    };
    const getSortCriteria = (item) => item.sort ?? getDefaultSortCriteria(item);

    for (const column of columns) {
      sorters[this.props.getColumnLabel(column.heading)] = function (a, b) {
        const sortCriteria = getSortCriteria(column);
        if (column.heading.columnType().type() === 'DATE') {
          return sortCriteria === 0
            ? 0
            : sortCriteria < 0
            ? Date.parse(b) - Date.parse(a)
            : Date.parse(a) - Date.parse(b);
        }
        if (column.heading.columnType().type() === 'INT') {
          return sortCriteria === 0 ? 0 : sortCriteria < 0 ? b - a : a - b;
        }
        return sortCriteria === 0
          ? 0
          : sortCriteria < 0
          ? String(b).localeCompare(String(a))
          : String(a).localeCompare(String(b));
      };
    }
    for (const row of rows) {
      sorters[this.props.getColumnLabel(row.heading)] = function (a, b) {
        const sortCriteria = getSortCriteria(row);
        if (row.heading.columnType().type() === 'DATE') {
          return sortCriteria === 0
            ? 0
            : sortCriteria < 0
            ? Date.parse(b) - Date.parse(a)
            : Date.parse(a) - Date.parse(b);
        }
        if (row.heading.columnType().type() === 'INT') {
          return sortCriteria === 0 ? 0 : sortCriteria < 0 ? b - a : a - b;
        }
        return sortCriteria === 0
          ? 0
          : sortCriteria < 0
          ? String(b).localeCompare(String(a))
          : String(a).localeCompare(String(b));
      };
    }

    return sorters;
  }

  render() {
    const {
      getChartOption,
      dataTable,
      getColumnLabel,
      isDashboard,
      identifier,
    } = this.props;

    const is4D = dataTable.headings().length === 4;
    const is5D = dataTable.headings().length === 5;
    const customColumns = getChartOption([
      PIVOT_CUSTOM_HEADINGS,
      PIVOT_COLUMNS_ID,
    ])
      ? getChartOption([PIVOT_CUSTOM_HEADINGS, PIVOT_COLUMNS_ID]).map((e) =>
          PivotTableHeading.fromServer(e),
        )
      : null;
    const customRows = getChartOption([PIVOT_CUSTOM_HEADINGS, PIVOT_ROWS_ID])
      ? getChartOption([PIVOT_CUSTOM_HEADINGS, PIVOT_ROWS_ID]).map((e) =>
          PivotTableHeading.fromServer(e),
        )
      : null;
    const showTotalsCol = getChartOption([PIVOT_SHOW_TOTALS, PIVOT_COLUMNS_ID])
      ? getChartOption([PIVOT_SHOW_TOTALS, PIVOT_COLUMNS_ID])
      : false;
    const showTotalsRow = getChartOption([PIVOT_SHOW_TOTALS, PIVOT_ROWS_ID])
      ? getChartOption([PIVOT_SHOW_TOTALS, PIVOT_ROWS_ID])
      : false;
    const style = isDashboard ? { margin: '10px' } : {};

    const initialColumnIndex = is4D ? 2 : is5D ? 3 : 1;
    const columnsRaw = customColumns
      ? this.getColumns(
          customColumns.map((e) => DataTableHeading.fromServer(e.heading)),
          dataTable,
        )
      : [dataTable.headings()[initialColumnIndex]];
    const columns = columnsRaw.map((cl) => getColumnLabel(cl));
    const columnsWithSort =
      customColumns || columnsRaw.map((e) => new PivotTableHeading(e));

    const rowsRaw = customRows
      ? this.getRows(
          customRows.map((e) => DataTableHeading.fromServer(e.heading)),
          customColumns.map((e) => DataTableHeading.fromServer(e.heading)),
          dataTable,
        )
      : dataTable.headings().filter((e, i) => i < initialColumnIndex);
    const rows = rowsRaw.map((cl) => getColumnLabel(cl));
    const rowsWithSort =
      customRows || rowsRaw.map((e) => new PivotTableHeading(e));

    const data = [];
    const customFormatters = {};
    dataTable.rows().forEach((row) => {
      const r = row.cells().reduce((obj, cell, j) => {
        if (obj instanceof DataTableCell) {
          customFormatters[getColumnLabel(dataTable.headings()[j - 1])] =
            cellFormatter(dataTable, getChartOption, j - 1, false);
          customFormatters[getColumnLabel(dataTable.headings()[j])] =
            cellFormatter(dataTable, getChartOption, j, false);
          return {
            [getColumnLabel(dataTable.headings()[j - 1])]: obj.value(),
            [getColumnLabel(dataTable.headings()[j])]: cell.value(),
          };
        }
        customFormatters[getColumnLabel(dataTable.headings()[j])] =
          cellFormatter(dataTable, getChartOption, j, false);
        return {
          ...obj,
          [getColumnLabel(dataTable.headings()[j])]: cell.value(),
        };
      });
      data.push(r);
    });

    const vals = [
      getColumnLabel(dataTable.headings()[dataTable.headings().length - 1]),
    ];

    const sorters = this.buildSorters(columnsWithSort, rowsWithSort);

    /*
			To correctly know how to build this presets and the available options, please check the documentation at
			https://github.com/plotly/react-pivottable
		 */
    const pivotPresets = {
      cols: columns,
      rows,
      vals,
      sorters,
      renderers: { TrevorTable }, // Check src/main/javascript/components/pivotTable/trevorTableRenderers.js
      rendererName: 'TrevorTable', // From the line above
      aggregators, // List of available aggregators: https://github.com/plotly/react-pivottable/blob/4be236ec81ed276d9d8ab264d7c3c5912e1882bd/src/Utilities.js#L422
      aggregatorName: 'Sum', // Name of desired aggregator from: https://github.com/plotly/react-pivottable/blob/4be236ec81ed276d9d8ab264d7c3c5912e1882bd/src/Utilities.js#L422
      customFormatters, // This is an additional preset not part of the default documentation. We added it to be able to pass the Trevor cell formatters to the TrevorPivotTable renderer
    };

    if (columns.length === 0) {
      // Total cells must be shown and the label should be changed to the value field label
      this.showTotalCells(showTotalsCol, true, vals[0]);
    } else if (rows.length === 0) {
      this.showTotalCells(true, showTotalsRow, vals[0]);
    } else {
      this.showTotalCells(showTotalsCol, showTotalsRow);
    }

    if (!isPivotColumnTypeSubtotalsSupported(dataTable.headings())) {
      this.waitForElm(
        `div#pivot-${identifier} table.pvtTable tbody th.pvtRowLabel`,
        `div#pivot-${identifier} table.pvtTable tbody tr`,
      ).then(() => {
        this.hideTotalCells();
      });
    }

    return (
      <div
        className={`dashboards ${pivot.container}`}
        style={style}
        id={`pivot-${identifier}`}
      >
        <PivotTableUI
          data={data}
          onChange={() => {}} // Is required by the component but we're not using it
          {...pivotPresets}
        />
      </div>
    );
  }
}

export default PivotTable;
